if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnailContainers: Array.from(this.querySelectorAll('[data-media-gallery-thumbnails]')),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.scheduleGalleryPreload = debounce(this.preloadNearbyGalleryImages.bind(this), 120);
        this._counterRaf = null;

        const galleryList = this.elements.viewer?.querySelector('ul.product__media-list.slider');
        const gallerySlides = galleryList?.querySelectorAll('.product__media-item');
        if (galleryList && gallerySlides?.length > 1) {
          galleryList.addEventListener(
            'scroll',
            () => {
              this.scheduleGalleryPreload();
              this.requestGalleryCounterSync();
            },
            { passive: true }
          );
          requestAnimationFrame(() => {
            this.preloadNearbyGalleryImages();
            this.syncMainGalleryCounter();
          });
        }

        if (this.elements.thumbnailContainers.length === 0) return;

        this.elements.viewer.addEventListener('slideChanged', () => this.preloadNearbyGalleryImages());
        this.elements.viewer.addEventListener('slideChanged', () => this.syncMainGalleryCounter());
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnailContainers.forEach((container) => {
          container.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      requestGalleryCounterSync() {
        if (this._counterRaf != null) return;
        this._counterRaf = requestAnimationFrame(() => {
          this._counterRaf = null;
          this.syncMainGalleryCounter();
        });
      }

      /**
       * Dawn's SliderComponent derives the index from scrollLeft / fixed step size, which breaks for
       * peek / scroll-snap layouts. Use whichever slide is closest to the viewport center instead.
       */
      syncMainGalleryCounter() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        const list = viewer.querySelector('ul.product__media-list.slider');
        const currentEl = viewer.querySelector('.slider-counter--current');
        const totalEl = viewer.querySelector('.slider-counter--total');
        if (!list || !currentEl || !totalEl) return;

        const slides = list.querySelectorAll('li.product__media-item');
        if (slides.length === 0) return;

        const centerX = list.scrollLeft + list.clientWidth * 0.5;
        let bestIndex = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const slideMid = slide.offsetLeft + slide.offsetWidth * 0.5;
          const d = Math.abs(slideMid - centerX);
          if (d < bestDist) {
            bestDist = d;
            bestIndex = i;
          }
        });

        const displayIndex = bestIndex + 1;
        if (currentEl.textContent !== String(displayIndex)) {
          currentEl.textContent = displayIndex;
        }
        const total = slides.length;
        if (totalEl.textContent !== String(total)) {
          totalEl.textContent = total;
        }
      }

      preloadNearbyGalleryImages() {
        const list = this.elements.viewer?.querySelector('ul.product__media-list.slider');
        if (!list) return;
        const extent = list.clientWidth;
        const viewLeft = list.scrollLeft - extent;
        const viewRight = list.scrollLeft + list.clientWidth + extent;
        list.querySelectorAll('.product__media-item').forEach((slide) => {
          const left = slide.offsetLeft;
          const right = left + slide.offsetWidth;
          if (right < viewLeft || left > viewRight) return;
          slide.querySelectorAll('img').forEach((img) => {
            if (img.loading === 'lazy') img.loading = 'eager';
            if (!img.complete) img.decode?.().catch(() => {});
          });
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        this.elements.thumbnailContainers.forEach((container) => {
          const thumbnail = container.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(container, thumbnail);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          this.elements.thumbnailContainers.forEach((container) => {
            const activeThumbnail = container.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          });

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnailContainers.length) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          this.requestGalleryCounterSync();
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnailContainers.length === 0) return;
        let announced = false;
        this.elements.thumbnailContainers.forEach((container) => {
          const activeThumbnail = container.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(container, activeThumbnail);
          if (activeThumbnail && !announced) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            announced = true;
          }
        });
      }

      setActiveThumbnail(thumbnailContainer, thumbnail) {
        if (!thumbnailContainer || !thumbnail) return;

        thumbnailContainer
          .querySelectorAll('[data-target] button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (thumbnailContainer.isSlideVisible(thumbnail, 10)) return;

        if (thumbnailContainer.slider)
          thumbnailContainer.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
